/**
 * DTOs para comunicação com o servidor DAV
 * Baseados nas especificações da API
 */

import { Order, OrderItem } from "../slices/orders/reducer";

// DTO para receber dados do servidor (GetDataToDavMobile)
export interface GetDataToDavMobileResponse {
  user: GetDataToDavMobileUserResponse;
  customers: GetDataToDavMobileCustomerResponse[];
  paymentMethods: GetDataToDavMobilePaymentMethodsResponse[];
  paymentForms: GetDataToDavMobilePaymentFormsResponse[];
  products: GetDataToDavMobileProductsResponse[];
}

export interface GetDataToDavMobileUserResponse {
  documentOwner: string;
  id: number;
  name: string;
  user: string;
  password: string;
  lastSyncDate: string;
}

export interface GetDataToDavMobileCustomerResponse {
  documentOwner: string;
  id: number;
  name: string;
}

export interface GetDataToDavMobilePaymentMethodsResponse {
  documentOwner: string;
  id: number;
  description: string;
  internalCode: number;
}

export interface GetDataToDavMobilePaymentFormsResponse {
  documentOwner: string;
  id: number;
  description: string;
  numberInstallments: number;
  interest: number;
  dayFirstQuote: number;
}

export interface GetDataToDavMobileProductsResponse {
  documentOwner: string;
  id: number;
  description: string;
  quantity: number;
  unitPrice: number;
  promotionPrice: number;
  promotionFinalDate: string;
}

// DTO para enviar pedidos ao servidor (ReceiveDataFromDavMobile)
export interface ReceiveDataFromDavMobileCreate {
  documentOwner: string;
  id: number;
  sellerId: number;
  customerId: number;
  dateEmission: string;
  status: number;
  amountProducts: number;
  amountPromotion: number;
  observation: string;
  paymentMethodId: number;
  paymentFormId: number;
  items: ReceiveDataFromDavMobileItemCreate[];
}

export interface ReceiveDataFromDavMobileItemCreate {
  documentOwner: string;
  idDav: number;
  idProduct: number;
  id: number;
  quantity: number;
  unitPrice: number;
  promotionPrice: number;
  promotionProrated: number;
  observation: string;
  promotionId?: number;
}

// Mappers para transformar entre formatos
export class OrderDTOMapper {
  /**
   * Converte pedido interno para DTO de envio ao servidor
   */
  static toReceiveDataFromDavMobile(order: Order): ReceiveDataFromDavMobileCreate {
    return {
      documentOwner: order.documentOwner,
      id: order.id,
      sellerId: order.sellerId,
      customerId: order.customerId,
      dateEmission: order.dateEmission,
      status: order.status,
      amountProducts: order.amountProducts,
      amountPromotion: order.amountPromotion,
      observation: order.observation,
      paymentMethodId: order.paymentMethodId,
      paymentFormId: order.paymentFormId,
      items: order.items.map((item: OrderItem, index: number) => ({
        documentOwner: item.documentOwner,
        idDav: item.idDav,
        idProduct: item.idProduct,
        id: index + 1, // ID sequencial do item
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        promotionPrice: item.promotionPrice,
        promotionProrated: item.promotionProrated || 0,
        observation: item.observation || "",
        promotionId: item.promotionId,
      })),
    };
  }

  /**
   * Converte dados recebidos do servidor para modelo interno
   */
  static fromGetDataToDavMobile(data: GetDataToDavMobileResponse) {
    return {
      user: data.user,
      customers: data.customers.map((customer) => ({
        documentOwner: customer.documentOwner,
        id: customer.id,
        name: customer.name,
      })),
      paymentMethods: data.paymentMethods.map((method) => ({
        documentOwner: method.documentOwner,
        id: method.id,
        description: method.description,
        internalCode: method.internalCode,
      })),
      paymentForms: data.paymentForms.map((form) => ({
        documentOwner: form.documentOwner,
        id: form.id,
        description: form.description,
        numberInstallments: form.numberInstallments,
        interest: form.interest,
        dayFirstQuote: form.dayFirstQuote,
      })),
      products: data.products.map((product) => ({
        documentOwner: product.documentOwner,
        id: product.id,
        description: product.description,
        quantity: product.quantity,
        unitPrice: product.unitPrice,
        promotionPrice: product.promotionPrice,
        promotionFinalDate: product.promotionFinalDate,
      })),
    };
  }
}
